/**
 * ProjectGemsService - Service for managing project-level gem aggregation
 * Follows Single Responsibility Principle for project gems operations
 */
import apiClient from '@/api/client';
export class ProjectGemsServiceError extends Error {
    projectId;
    constructor(message, projectId) {
        super(message);
        this.projectId = projectId;
        this.name = 'ProjectGemsServiceError';
    }
}
export class ProjectGemsService {
    /**
     * Fetch aggregated gems for a specific project
     * @param projectId - The project identifier
     * @returns Promise<SessionGems> - Aggregated gems from all sessions in the project
     */
    static async getProjectGems(projectId) {
        try {
            if (!projectId || typeof projectId !== 'string') {
                throw new ProjectGemsServiceError('Invalid project ID provided', projectId);
            }
            const encodedProjectId = encodeURIComponent(projectId);
            const response = await apiClient.get(`/projects/${encodedProjectId}/gems`);
            if (!response.data) {
                throw new ProjectGemsServiceError(`No response data received for project ${projectId}`, projectId);
            }
            const projectGems = response.data.data;
            return projectGems;
        }
        catch (error) {
            console.error('[ProjectGemsService] Error fetching project gems:', error);
            if (error instanceof ProjectGemsServiceError) {
                throw error;
            }
            // Handle API errors
            if (error.response?.status === 404) {
                throw new ProjectGemsServiceError(`Project ${projectId} not found or has no gems`, projectId);
            }
            if (error.response?.status >= 500) {
                throw new ProjectGemsServiceError(`Server error while fetching gems for project ${projectId}`, projectId);
            }
            throw new ProjectGemsServiceError(`Failed to fetch gems for project ${projectId}: ${error.message || 'Unknown error'}`, projectId);
        }
    }
    /**
     * Check if a project has any gems across its sessions
     * @param projectId - The project identifier
     * @returns Promise<boolean> - True if project has gems, false otherwise
     */
    static async hasProjectGems(projectId) {
        try {
            const gems = await this.getProjectGems(projectId);
            const hasGems = Object.keys(gems).length > 0;
            return hasGems;
        }
        catch (error) {
            console.warn('[ProjectGemsService] Error checking project gems, assuming no gems:', error.message || 'Unknown error');
            // Check if it's a network error (backend down)
            if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error') || error.message?.includes('timeout')) {
                console.warn('[ProjectGemsService] Backend appears to be down - this would normally check for gems');
                // In production, you might want to return true here for known projects with gems
                // For now, return false to avoid showing non-functional buttons
            }
            return false;
        }
    }
    /**
     * Count total gems across all types in a project
     * @param gems - SessionGems object
     * @returns number - Total count of gems
     */
    static countTotalGems(gems) {
        return Object.values(gems).reduce((total, items) => {
            return total + (Array.isArray(items) ? items.length : 0);
        }, 0);
    }
    /**
     * Get gem types sorted by count (descending)
     * Use GemsService for complex sorting logic
     * @param gems - SessionGems object
     * @returns string[] - Array of gem type names sorted by count
     */
    static getSortedGemTypes(gems) {
        return Object.keys(gems).sort((a, b) => {
            const countA = Array.isArray(gems[a]) ? gems[a].length : 0;
            const countB = Array.isArray(gems[b]) ? gems[b].length : 0;
            return countB - countA;
        });
    }
    /**
     * Get project gems summary for display
     * @param projectId - The project identifier
     * @returns Promise<ProjectGemsSummary> - Summary of project gems
     */
    static async getProjectGemsSummary(projectId) {
        try {
            const gems = await this.getProjectGems(projectId);
            const sortedTypes = this.getSortedGemTypes(gems);
            const totalGems = this.countTotalGems(gems);
            const topTypes = sortedTypes.slice(0, 3).map(type => ({
                type,
                count: Array.isArray(gems[type]) ? gems[type].length : 0
            }));
            const summary = {
                projectId,
                totalGems,
                gemTypes: sortedTypes,
                topTypes
            };
            return summary;
        }
        catch (error) {
            console.error('[ProjectGemsService] Error generating project gems summary:', error);
            throw error;
        }
    }
}
